import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MagicLogin — claims a magic token and redirects to the matching role home.
 *
 * Route: /auth/magic/:token
 *
 * Magic tokens are strictly single-use. React 18 StrictMode fires effects twice
 * in development, so without a guard the second call gets 410 and the user
 * briefly sees a "Sign-in failed" flash before the setTimeout redirect runs.
 * `claimedRef` ensures we hit the API exactly once per mount.
 */
const MagicLogin = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const claimedRef = useRef(false);

  useEffect(() => {
    if (claimedRef.current) return;
    claimedRef.current = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/booking/magic/${token}`);
        localStorage.setItem('token', r.data.token);
        setStatus('ok');
        // Redirect based on role
        setTimeout(() => {
          const role = r.data.user_role;
          const path = role === 'client' ? '/client/home' : role === 'talent' ? '/talent/home' : '/admin';
          window.location.href = path;
        }, 1200);
      } catch (e) {
        setStatus('error');
        setError(e.response?.data?.detail || 'Invalid or expired link');
      }
    })();
  }, [token]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600 mb-3" />
        <p className="text-sm text-gray-600">Signing you in…</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
        <h1 className="text-xl font-semibold">Sign-in failed</h1>
        <p className="text-sm text-gray-600 mt-1">{error}</p>
        <Button className="mt-4" onClick={() => navigate('/login')}>Go to Login</Button>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex flex-col items-center justify-center">
      <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-3" />
      <p className="text-sm text-gray-700">Signed in! Redirecting…</p>
    </div>
  );
};

export default MagicLogin;
